import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import settingsService from '../../../../../services/settings.service';
import usersService from '../../../../../services/users.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { confirmAction, showError, showSuccess } from '../../../../../utils/ecxSwal';
import { ClinicInfoPanel } from '../../clinicsettings/info/clinicInfoParts';
import ClinicSettingsCancelButton from '../../clinicsettings/subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../clinicsettings/subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../clinicsettings/subparts/ClinicSettingsListSkeleton';
import ClinicSettingsSectionHeader from '../../clinicsettings/subparts/ClinicSettingsSectionHeader';
import {
    CHANNEL_KEYS,
    GLOBAL_SCOPE,
    REMINDER_CATEGORIES,
    ReminderOverviewStats,
    ReminderScopeSelector,
    ReminderSettingsPanels,
    TOGGLE_KEYS,
    getEcpDisplayName,
    normalizeSettings,
    stripToReminderPayload,
    toApiPayload,
} from '../reminderSettingsShared';

const ReminderCard = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [searchParams] = useSearchParams();
    const [settings, setSettings] = useState(null);
    const [selectedScope, setSelectedScope] = useState(GLOBAL_SCOPE);
    const [ecpUsers, setEcpUsers] = useState([]);
    const [ecpLoading, setEcpLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [settingsMissing, setSettingsMissing] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        formState: { errors },
    } = useForm();

    const formValues = watch();

    const selectedEcp = useMemo(
        () => ecpUsers.find((ecp) => ecp.id === selectedScope) || null,
        [ecpUsers, selectedScope]
    );

    const isGlobalScope = selectedScope === GLOBAL_SCOPE;

    const scopeLabel = isGlobalScope
        ? 'Global (Clinic Default)'
        : getEcpDisplayName(selectedEcp);

    const scopeSubtitle = isGlobalScope
        ? 'Configure clinic-wide default reminder settings applied to all ECPs'
        : `Manage reminder settings for ${scopeLabel}`;

    const fetchEcpUsers = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        setEcpLoading(true);

        try {
            const response = await settingsService.getUsersByRole(clinicId, 'ECP');
            setEcpUsers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error(error);
            setEcpUsers([]);
            showError({
                title: 'Load Failed',
                text: 'Unable to load ECP list. Please try again.',
            });
        } finally {
            setEcpLoading(false);
        }
    }, [clinicId]);

    const fetchSettings = useCallback(async (scope = selectedScope) => {
        if (!clinicId) {
            return;
        }

        const isScopeGlobal = scope === GLOBAL_SCOPE;

        setIsLoading(true);
        setLoadFailed(false);
        setSettingsMissing(false);

        try {
            const response = isScopeGlobal
                ? await settingsService.getReminderSettings(clinicId)
                : await usersService.getUserReminder(clinicId, scope);

            const normalized = normalizeSettings(response.data);
            setSettings(normalized);
            reset(normalized);
        } catch (error) {
            console.error(error);

            if (!isScopeGlobal && error?.response?.status === 404) {
                setSettings(null);
                setSettingsMissing(true);
            } else {
                setLoadFailed(true);
                showError({
                    title: 'Load Failed',
                    text: isScopeGlobal
                        ? 'Unable to load clinic reminder settings. Please try again.'
                        : 'Unable to load ECP reminder settings. Please try again.',
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [clinicId, reset, selectedScope]);

    useEffect(() => {
        fetchEcpUsers();
    }, [fetchEcpUsers]);

    useEffect(() => {
        const ecpId = searchParams.get('ecp');
        if (ecpId) {
            setSelectedScope(ecpId);
        }
    }, [searchParams]);

    useEffect(() => {
        setIsEditing(false);
        fetchSettings(selectedScope);
    }, [selectedScope, fetchSettings]);

    const allItems = useMemo(
        () => REMINDER_CATEGORIES.flatMap((category) => category.items),
        []
    );

    const currentValues = isEditing ? formValues : settings;

    const enabledCount = useMemo(() => {
        if (!currentValues) {
            return 0;
        }

        return TOGGLE_KEYS.filter((key) => currentValues[key]).length;
    }, [currentValues]);

    const activeChannels = useMemo(() => {
        if (!currentValues) {
            return 0;
        }

        return CHANNEL_KEYS.filter((key) => currentValues[key]).length;
    }, [currentValues]);

    const handleEdit = () => {
        reset(settings);
        setIsEditing(true);
    };

    const handleCancel = () => {
        reset(settings);
        setIsEditing(false);
    };

    const handleScopeChange = (nextScope) => {
        if (isEditing) {
            setIsEditing(false);
        }

        setSelectedScope(nextScope);
    };

    const applyGlobalSettingsToEcp = useCallback(async (userId) => {
        const globalResponse = await settingsService.getReminderSettings(clinicId);
        const payload = stripToReminderPayload(globalResponse.data);
        const response = await usersService.updateUserReminder(clinicId, userId, payload);
        return normalizeSettings(response.data);
    }, [clinicId]);

    const handleResetToGlobal = async () => {
        if (isGlobalScope || !selectedScope) {
            return;
        }

        const ecpName = getEcpDisplayName(selectedEcp);
        const result = await confirmAction({
            title: 'Reset to Global Settings?',
            html: `<p>This will replace <strong>${ecpName}</strong>'s reminder settings with the clinic's global defaults.</p>`,
            iconClass: 'bi bi-arrow-counterclockwise',
            confirmText: 'Reset to Global',
        });

        if (!result.isConfirmed) {
            return;
        }

        setIsResetting(true);

        try {
            const normalized = await applyGlobalSettingsToEcp(selectedScope);
            setSettings(normalized);
            reset(normalized);
            setSettingsMissing(false);
            setIsEditing(false);

            await showSuccess({
                title: 'Settings Reset',
                text: `${ecpName}'s reminder settings were reset to global defaults.`,
            });
        } catch (error) {
            console.error(error);
            showError({
                title: 'Reset Failed',
                text: 'Unable to reset reminder settings. Please try again.',
            });
        } finally {
            setIsResetting(false);
        }
    };

    const onSubmit = async (data) => {
        setIsSaving(true);

        try {
            const response = isGlobalScope
                ? await settingsService.updateReminderSettings(clinicId, toApiPayload(data))
                : await usersService.updateUserReminder(clinicId, selectedScope, stripToReminderPayload(data));

            const normalized = normalizeSettings(response.data);
            setSettings(normalized);
            reset(normalized);
            setSettingsMissing(false);
            setIsEditing(false);

            await showSuccess({
                title: 'Settings Saved',
                text: isGlobalScope
                    ? 'Global reminder settings were updated successfully.'
                    : `${scopeLabel}'s reminder settings were updated successfully.`,
            });
        } catch (error) {
            console.error(error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update reminder settings. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const toolbarActions = isEditing ? (
        <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
    ) : (
        <button
            type="button"
            className="btn btn-sm btn-light-primary rounded-pill"
            onClick={handleEdit}
        >
            <i className="bi bi-pencil me-1"></i>
            Edit Settings
        </button>
    );

    const scopeSelector = (
        <ReminderScopeSelector
            selectedScope={selectedScope}
            ecpUsers={ecpUsers}
            ecpLoading={ecpLoading}
            isEditing={isEditing}
            isSaving={isSaving}
            isResetting={isResetting}
            onScopeChange={handleScopeChange}
            onResetToGlobal={handleResetToGlobal}
        />
    );

    if (isLoading) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="Reminder Overview"
                    icon="bi-bell"
                    subtitle={scopeSubtitle}
                />
                {scopeSelector}
                <ReminderOverviewStats allItemsCount={0} enabledCount={0} activeChannels={0} />
                {REMINDER_CATEGORIES.map((category) => (
                    <ClinicInfoPanel key={category.title} title={category.title} icon={category.icon}>
                        <ClinicSettingsListSkeleton rows={category.items.length} />
                    </ClinicInfoPanel>
                ))}
            </>
        );
    }

    if (loadFailed) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="Reminder Overview"
                    icon="bi-bell"
                    subtitle={scopeSubtitle}
                />
                {scopeSelector}
                <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3">
                    <span>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Failed to load reminder settings.
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger rounded-pill"
                        onClick={() => fetchSettings(selectedScope)}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Retry
                    </button>
                </div>
            </>
        );
    }

    if (settingsMissing) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="Reminder Overview"
                    icon="bi-bell"
                    subtitle={scopeSubtitle}
                />
                {scopeSelector}
                <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3">
                    <span>
                        <i className="bi bi-info-circle me-2"></i>
                        No custom reminder settings found for {scopeLabel}. Apply global defaults to get started.
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-warning rounded-pill"
                        onClick={handleResetToGlobal}
                        disabled={isResetting}
                    >
                        <i className={`bi bi-arrow-counterclockwise me-1 ${isResetting ? 'ecx-refresh-spin' : ''}`}></i>
                        Apply Global Settings
                    </button>
                </div>
            </>
        );
    }

    if (!settings) {
        return null;
    }

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Reminder Overview"
                icon="bi-bell"
                subtitle={scopeSubtitle}
                actions={
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="ecx-page-chip">
                            <i className={`bi ${isGlobalScope ? 'bi-globe2' : 'bi-person-badge'}`}></i>
                            {scopeLabel}
                        </span>
                        <span className="ecx-page-chip">
                            <i className="bi bi-grid-3x3-gap"></i>
                            {allItems.length} settings
                        </span>
                        <span className="ecx-page-chip">
                            <i className="bi bi-check-circle"></i>
                            {enabledCount} enabled
                        </span>
                        {toolbarActions}
                    </div>
                }
            />

            {scopeSelector}

            <ReminderOverviewStats
                allItemsCount={allItems.length}
                enabledCount={enabledCount}
                activeChannels={activeChannels}
            />

            <form onSubmit={handleSubmit(onSubmit)} className="reminder-settings-form">
                <ReminderSettingsPanels
                    values={currentValues}
                    isEditing={isEditing}
                    register={register}
                    control={control}
                    errors={errors}
                    fieldIdPrefix={selectedScope}
                />

                {isEditing && (
                    <div className="reminder-settings-form__footer">
                        <ClinicSettingsFormFooter isSaving={isSaving} submitLabel="Save Changes" />
                    </div>
                )}
            </form>
        </>
    );
};

export default ReminderCard;
